import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { AlertTriangle, RefreshCw, Route } from 'lucide-react'
import {
  ServerApiClient,
  type FounderTourAnalyticsResponse,
  type FounderTourStep,
} from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

/**
 * Guided-tour funnel for the founder console.
 *
 * Three questions, in the order they matter: are people finishing, where do they
 * leave, and is the tour still pointing at real elements. The third is the one
 * that has no equivalent in a capture-and-replay demo — a live-app tour breaks
 * when a component is renamed, and the anchor-drift panel is the production
 * canary CI cannot be, because it sees every real viewport and data shape.
 */

type Props = { topInsetPx?: number }

const panel: CSSProperties = {
  border: '1px solid #d9e2df',
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}
const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  background: '#fff',
  color: '#15343b',
  borderRadius: 7,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}
const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  color: '#5d6d72',
  fontWeight: 800,
  borderBottom: '1px solid #e4ece9',
  padding: '8px 10px',
  whiteSpace: 'nowrap',
}
const tdStyle: CSSProperties = {
  fontSize: 12,
  color: '#223236',
  borderBottom: '1px solid #eef3f1',
  padding: '8px 10px',
  verticalAlign: 'top',
}
const numTd: CSSProperties = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
const sectionTitle: CSSProperties = { fontSize: 13, fontWeight: 800, color: '#15343b', margin: '0 0 2px' }
const sectionNote: CSSProperties = { fontSize: 11, color: '#5d6d72', margin: '0 0 10px' }

const fmtPct = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(Number(v)) ? '-' : `${Number(v).toFixed(1)}%`
const fmtNum = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(Number(v)) ? '-' : Number(v).toLocaleString()
const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const fmtDwell = (ms: number | null | undefined): string => {
  const n = Number(ms)
  if (!Number.isFinite(n) || n <= 0) return '-'
  if (n >= 60_000) return `${(n / 60_000).toFixed(1)} min`
  return `${Math.round(n / 1000)} s`
}

/** Retention below this reads as a genuine leak rather than natural tail-off. */
const LEAK_THRESHOLD = 70

function StepFunnel({ steps }: { steps: FounderTourStep[] }) {
  if (!steps.length) return <div style={{ ...tdStyle, borderBottom: 'none' }}>No steps viewed in this window.</div>
  const max = Math.max(...steps.map((s) => s.sessionsReached), 1)
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={thStyle}>#</th>
          <th style={thStyle}>Step</th>
          <th style={thStyle}>Reach</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Sessions</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Of starters</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Kept from prev</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Median time</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Anchor misses</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((step) => {
          const leaking = step.stepRetentionPct < LEAK_THRESHOLD && step.stepIndex > 0
          return (
            <tr key={`${step.tourSlug}-${step.stepId}`}>
              <td style={numTd}>{step.stepIndex + 1}</td>
              <td style={tdStyle}>
                <div style={{ fontWeight: 700 }}>{step.stepId}</div>
                {step.chapterId ? <div style={{ color: '#5d6d72', fontSize: 11 }}>{step.chapterId}</div> : null}
              </td>
              <td style={{ ...tdStyle, width: 180 }}>
                <div style={{ background: '#eef3f1', borderRadius: 4, height: 10, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.round((step.sessionsReached / max) * 100)}%`,
                      background: leaking ? '#c0563c' : '#2c6bed',
                      height: '100%',
                    }}
                  />
                </div>
              </td>
              <td style={numTd}>{fmtNum(step.sessionsReached)}</td>
              <td style={numTd}>{fmtPct(step.pctOfStarters)}</td>
              <td style={{ ...numTd, color: leaking ? '#8c2f2f' : undefined, fontWeight: leaking ? 800 : undefined }}>
                {fmtPct(step.stepRetentionPct)}
              </td>
              <td style={numTd}>{fmtDwell(step.medianDwellMs)}</td>
              <td style={{ ...numTd, color: step.anchorMisses > 0 ? '#8c2f2f' : undefined }}>
                {step.anchorMisses > 0 ? fmtNum(step.anchorMisses) : '-'}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export function FounderToursDashboard({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const [data, setData] = useState<FounderTourAnalyticsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [windowDays, setWindowDays] = useState(30)

  // Voice freshness per tour: published copy vs served narration manifest,
  // recomputed with the generator's exact hash recipe. Runs whenever the tab
  // loads — the pull-side of the nightly staleness cron.
  const [voiceHealth, setVoiceHealth] = useState<Record<string, string>>({})
  useEffect(() => {
    let cancelled = false
    const hash12 = async (text: string) => {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
      return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 12)
    }
    const renderDefaults = (text: string, tokens: Array<{ key: string; defaultValue: string }> | null) =>
      String(text).replace(/\{\{\s*([a-zA-Z0-9_.:-]+)\s*\}\}/g, (_, key) =>
        tokens?.find((t) => t.key === key)?.defaultValue ?? '')
    const check = async (slug: string): Promise<string> => {
      const pub = await fetch(`/api/tours/published/${encodeURIComponent(slug)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      if (!pub?.doc) return 'no doc'
      const manifest = await fetch(`/tour-audio/${encodeURIComponent(slug)}/manifest.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      const voiceId = pub.doc.theme?.voiceId || manifest?.voiceId || 'EXAVITQu4vr4xnSDxMaL'
      let fresh = 0; let bad = 0
      for (const ch of pub.doc.chapters || []) {
        for (const step of ch.steps || []) {
          if (step.voice && 'script' in step.voice && step.voice.script === null) continue
          const raw = step.voice?.script ?? step.widget?.body
          if (!raw) continue
          const text = renderDefaults(raw, pub.doc.tokens).replace(/\s+/g, ' ').trim()
          if (!text) continue
          const want = await hash12(`${voiceId}|eleven_multilingual_v2|${text}`)
          if (manifest?.steps?.[step.id]?.hash === want) fresh++
          else bad++
        }
      }
      if (!fresh && !bad) return 'no voice'
      return bad ? `${bad} stale` : 'fresh'
    }
    void (async () => {
      const out: Record<string, string> = {}
      for (const t of data?.tours || []) out[t.tourSlug] = await check(t.tourSlug)
      if (!cancelled) setVoiceHealth(out)
    })()
    return () => { cancelled = true }
  }, [data])
  const [slug, setSlug] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await ensureApiSessionReady()
      setData(await api.getFounderTourAnalytics({ windowDays, slug }))
    } catch (err: unknown) {
      setError(String((err as Error)?.message || err || 'Failed to load tour analytics.'))
    } finally {
      setLoading(false)
    }
  }, [api, windowDays, slug])

  useEffect(() => { void load() }, [load])

  const steps = data?.steps || []
  const alerts = data?.anchorAlerts || []
  const sessions = data?.sessions || []
  const variants = data?.variants || []
  // A lone base arm is noise — the table earns its place only once a real
  // ?fg_tour_v= arm has traffic to compare against.
  const showVariants = variants.some((v) => v.variant !== null)

  return (
    <div style={{ height: `calc(100vh - ${topInsetPx}px)`, marginTop: topInsetPx, overflowY: 'auto', padding: '14px 16px 40px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ ...sectionTitle, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <Route size={16} /> Guided tours
          </h2>
          <span style={{ flex: 1 }} />
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              style={{ ...buttonStyle, background: windowDays === days ? '#15343b' : '#fff', color: windowDays === days ? '#fff' : '#15343b' }}
              onClick={() => setWindowDays(days)}
            >
              {days}d
            </button>
          ))}
          {slug ? (
            <button style={buttonStyle} onClick={() => setSlug(null)}>Clear "{slug}"</button>
          ) : null}
          <button style={buttonStyle} onClick={() => void load()} disabled={loading}>
            <RefreshCw size={13} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error ? (
          <div style={{ ...panel, padding: 12, borderColor: '#e5b4b4', background: '#fdeeee', color: '#8c2f2f', fontSize: 12 }}>
            {error}
          </div>
        ) : null}

        {/* Anchor drift first: a broken tour outranks a soft funnel, because a
            prospect is watching it right now. */}
        {alerts.length ? (
          <section style={{ ...panel, padding: 12, borderColor: '#e5b4b4', background: '#fdeeee' }}>
            <h3 style={{ ...sectionTitle, color: '#8c2f2f', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={14} /> Anchors that stopped resolving
            </h3>
            <p style={sectionNote}>
              These steps fell back for real visitors — the element they point at was renamed or no longer renders.
              Fix the anchor, or repoint the step.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Tour</th>
                  <th style={thStyle}>Step</th>
                  <th style={thStyle}>Anchor</th>
                  <th style={thStyle}>Kind</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Misses</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Sessions</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={`${a.tourSlug}-${a.stepId}-${a.anchorId}`}>
                    <td style={tdStyle}>{a.tourSlug}</td>
                    <td style={tdStyle}>{a.stepId}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{a.anchorId}</td>
                    <td style={tdStyle}>{a.anchorKind || '-'}</td>
                    <td style={numTd}>{fmtNum(a.misses)}</td>
                    <td style={numTd}>{fmtNum(a.sessions)}</td>
                    <td style={numTd}>{fmtDate(a.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <section style={{ ...panel, padding: 12 }}>
          <h3 style={sectionTitle}>Tours</h3>
          <p style={sectionNote}>
            Completion is finishes ÷ starts. Intent is a documented linear model — 40% completion, 15% dwell,
            15% any CTA, 20% identified, 10% depth after the tour.
          </p>
          {data?.tours.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Tour</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Starts</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Completion</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Engaged</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Leads</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Median intent</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Demo / embed</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Anchor misses</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Voice</th>
                </tr>
              </thead>
              <tbody>
                {data.tours.map((t) => (
                  <tr key={t.tourSlug} data-testid="founder-tour-row" style={{ cursor: 'pointer' }} onClick={() => setSlug(t.tourSlug)}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{t.tourSlug}</td>
                    <td style={numTd}>{fmtNum(t.starts)}</td>
                    <td style={numTd}>{fmtPct(t.completionPct)}</td>
                    <td style={numTd}>{fmtPct(t.engagementPct)}</td>
                    <td style={numTd}>{fmtNum(t.leads)}</td>
                    <td style={numTd}>{t.medianIntent.toFixed(1)}</td>
                    <td style={numTd}>{fmtNum(t.demoSessions)} / {fmtNum(t.embedSessions)}</td>
                    <td style={{ ...numTd, color: t.anchorMisses > 0 ? '#8c2f2f' : undefined }}>
                      {t.anchorMisses > 0 ? fmtNum(t.anchorMisses) : '-'}
                    </td>
                    <td style={{ ...numTd, color: /stale/.test(voiceHealth[t.tourSlug] || '') ? '#8c2f2f' : undefined }} data-testid="tour-voice-health">
                      {voiceHealth[t.tourSlug] || '…'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: '#5d6d72' }}>
              No tour activity in this window. Tours are flag-gated — open one with
              {' '}<code>?fg_tour=cfo-first-look</code>.
            </div>
          )}
        </section>

        {showVariants ? (
          <section style={{ ...panel, padding: 12 }}>
            <h3 style={sectionTitle}>Variants</h3>
            <p style={sectionNote}>
              Arms come from <code>?fg_tour_v=</code> links; sessions without one count as base.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Tour</th>
                  <th style={thStyle}>Arm</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Starts</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Completion</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Leads</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Lead %</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Median intent</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => (
                  <tr key={`${v.tourSlug}-${v.variant ?? 'base'}`} data-testid="tour-variant-row">
                    <td style={tdStyle}>{v.tourSlug}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{v.variant ?? 'base'}</td>
                    <td style={numTd}>{fmtNum(v.starts)}</td>
                    <td style={numTd}>{fmtPct(v.completionPct)}</td>
                    <td style={numTd}>{fmtNum(v.leads)}</td>
                    <td style={numTd}>{fmtPct(v.leadPct)}</td>
                    <td style={numTd}>{v.medianIntent.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <section style={{ ...panel, padding: 12 }}>
          <h3 style={sectionTitle}>Where people leave</h3>
          <p style={sectionNote}>
            "Kept from prev" is the drop-off signal — a step below {LEAK_THRESHOLD}% is losing people, not tailing off.
          </p>
          <StepFunnel steps={steps} />
        </section>

        <section style={{ ...panel, padding: 12 }}>
          <h3 style={sectionTitle}>Recent sessions</h3>
          <p style={sectionNote}>Each row is one visitor's run. Intent ranks who is worth a follow-up.</p>
          {sessions.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Started</th>
                  <th style={thStyle}>Tour</th>
                  <th style={thStyle}>Reached</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Steps</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Completion</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Time</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Intent</th>
                  <th style={thStyle}>Signals</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.sessionId}>
                    <td style={tdStyle}>{fmtDate(s.startedAt)}</td>
                    <td style={tdStyle}>{s.tourSlug}</td>
                    <td style={tdStyle}>{s.lastStepId || '-'}</td>
                    <td style={numTd}>{s.stepsViewed}{s.stepCount ? ` / ${s.stepCount}` : ''}</td>
                    <td style={numTd}>{fmtPct(s.completionPct)}</td>
                    <td style={numTd}>{fmtDwell(s.dwellMs)}</td>
                    <td style={{ ...numTd, fontWeight: s.intent >= 50 ? 800 : undefined }}>{s.intent.toFixed(0)}</td>
                    <td style={tdStyle}>
                      {[
                        s.finished ? 'finished' : null,
                        s.identified ? 'lead' : null,
                        s.embed ? 'embed' : null,
                        s.isDemo ? 'demo' : 'customer',
                      ].filter(Boolean).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: '#5d6d72' }}>No sessions in this window.</div>
          )}
        </section>
      </div>
    </div>
  )
}
